<script setup>
  import { NMenu, NIcon, NText } from 'naive-ui'
  import { h, computed, ref, onMounted, inject } from 'vue'
  import ArsLogoLight from '@/assets/images/logo.png'
  import { SignOutAlt } from '@vicons/fa'
  import { useRoute, RouterLink, useRouter } from 'vue-router'
  import { useAuthStore } from '@/stores/auth'
  import { log } from '@/services/log'

  const props = defineProps({
    vertical: Boolean,
    isNavTop: Boolean,
  })

  const mode = computed(() => (props.vertical ? 'vertical' : 'horizontal'))

  const appStoreFn = inject('appStoreFn')
  const appStore = appStoreFn()

  const authStore = useAuthStore()
  const router = useRouter()

  const route = useRoute()
  const requiresAuth = computed(() => route?.meta?.requiresAuth ?? false)
  const injectedMenuOptions = inject('menuOptions') ?? []

  const logoImage = ref(null)
  onMounted(() => {
    logoImage.value = ArsLogoLight
  })

  const renderIcon = (option) => {
    if (!option.icon) return null
    return h(NIcon, null, { default: option.icon })
  }

  const renderLabel = (option) => {
    if (option?.to) return h(RouterLink, { to: option.to }, { default: () => option.label })
    else if (option.key === 'logo')
      return h(
        'div',
        {
          style: 'display: flex; flex-direction: column; align-items: center;',
        },
        [
          h('img', { src: logoImage.value, style: 'height: 1.5rem; margin-bottom: .25rem;' }),
          appStore.showWelcomeTitle && !props.isNavTop
            ? h(
                'span',
                {
                  style: `font-size: .85rem; ${appStore.appTheme !== 'light' ? 'color: white;' : 'color: black;'}`,
                },
                `WELCOME ${authStore?.user?.username?.toUpperCase()}`
              )
            : null,
        ]
      )
    else return h(NText, { type: 'span' }, { default: () => option.label })
  }

  const filterMenuOptions = (options, canSee, isTechnician) => {
    return options
      .map((option) => {
        if (option.items) {
          const filteredItems = filterMenuOptions(option.items, canSee, isTechnician)
          if (filteredItems.length > 0) {
            return { ...option, items: filteredItems }
          }
        } else if (option.checkPermission === false || isTechnician) {
          return option
        } else if (option.permissionUri && canSee(option?.permissionUri)) {
          return option
        }
        return null
      })
      .filter(Boolean)
  }

  const menuOptions = computed(() => {
    const isTechnician = authStore?.user?.isTechnician ?? false
    return [
      {
        key: 'logo',
        label: '',
      },
      ...filterMenuOptions(injectedMenuOptions, authStore.canSee, isTechnician),
      {
        key: 'logout',
        icon: SignOutAlt.render,
      },
    ]
  })

  const handleUserMenu = (key) => {
    const actions = {
      profile: () => {
        const externalId = authStore.user.externalId
        const path = `/users/mono?q=${externalId}`
        router.push(path)
      },
      changePassword: () => {
        appStore.userResetInfo = authStore.user
        appStore.showResetPasswordModal = true
      },
      about: () => appStore.setAboutModal(true),
      logout: () => authStore.logout(),
    }
    const action = actions[key]
    if (action) {
      action()
    } else {
      log(`Unhandled key: ${key}, in handleUserMenu trigger in navigation menu`)
    }
  }
</script>

<template>
  <NMenu
    id="arsSiteMenu"
    v-if="requiresAuth"
    :options="menuOptions"
    children-field="items"
    :render-icon="renderIcon"
    :render-label="renderLabel"
    :mode="mode"
    @update:value="handleUserMenu"
  />
</template>
<style>
  #arsSiteMenu > .n-menu-item:last-child {
    position: absolute;
    bottom: 0;
  }
</style>
